import { supabase } from './supabase'

/**
 * Log an activity to the activity_log table
 * @param {string} action - The action type (e.g., 'job_created', 'property_updated')
 * @param {string} entityType - The entity type ('job', 'property', 'crew', 'user')
 * @param {string} entityId - The ID of the affected entity (optional)
 * @param {string} description - Human-readable description of the action
 * @param {object} metadata - Additional metadata (optional)
 */
export async function logActivity(action, entityType, entityId, description, metadata = {}) {
  if (!supabase) {
    console.log('[Activity]', action, entityType)
    return
  }

  try {
    const { error } = await supabase.from('activity_log').insert([{
      action,
      entity_type: entityType,
      entity_id: entityId,
      description,
      metadata,
      created_at: new Date().toISOString(),
    }])
    if (error) console.warn('Failed to log activity:', error.message)
  } catch (e) {
    console.warn('Activity logging error:', e.message)
  }
}

/**
 * Fetch recent activity logs with pagination
 * @param {number} limit - Number of logs to fetch per page
 * @param {number} offset - Number of logs to skip
 * @param {string} entityType - Optional filter by entity type
 * @returns {Promise<{data: Array, count: number}>} Activity logs with total count
 */
export async function getActivityLogs(limit = 50, offset = 0, entityType = null) {
  if (!supabase) return { data: [], count: 0 }

  try {
    let query = supabase
      .from('activity_log')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })

    if (entityType && entityType !== 'all') {
      query = query.eq('entity_type', entityType)
    }

    query = query.range(offset, offset + limit - 1)

    const { data, error, count } = await query
    if (error) throw error
    return { data: data || [], count: count || 0 }
  } catch (e) {
    console.warn('Failed to fetch activity logs:', e.message)
    return { data: [], count: 0 }
  }
}

// Action types for consistency
export const ACTIONS = {
  // Jobs
  JOB_CREATED: 'job_created',
  JOB_UPDATED: 'job_updated',
  JOB_DELETED: 'job_deleted',
  JOB_STATUS_CHANGED: 'job_status_changed',
  // Properties
  PROPERTY_CREATED: 'property_created',
  PROPERTY_UPDATED: 'property_updated',
  PROPERTY_DELETED: 'property_deleted',
  // Crew
  CREW_ADDED: 'crew_added',
  CREW_DELETED: 'crew_deleted',
  CREW_ACTIVATED: 'crew_activated',
  CREW_DEACTIVATED: 'crew_deactivated',
  // User
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  PASSWORD_CHANGED: 'password_changed',
}
